import {
  useCallback,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useApiClient, useModuleAvailability } from "@playbookai/api-clients";
import {
  getKpiCatalogItem,
  listKpiCatalogPage,
  useKpiSource,
  useKpiCacheScope,
  type KpiCatalogPage,
} from "@playbookai/dashboard-client";
import {
  KpiCatalogProvider,
  KpiSourceResolverProvider,
  type KpiCatalog,
  type KpiSourceResolver,
  type KpiSourceResult,
} from "@playbookai/ui";
import type { KpiDef } from "@playbookai/types";
import type { KpiTileRange } from "@playbookai/widget-schemas";

/**
 * The host half of the KPI Tile's binding: the pick-list a KPI is chosen from,
 * and the resolution of the KPI a tile already names.
 *
 * It lives in the shell because the shell is the composition root — `@playbookai/ui`
 * is data-blind and `packages/solve-feature` may not import `kpi-feature`, so
 * the one place that can see both the design-system seams and a KPI client is
 * here (the same reason `WidgetEntityResolverWiring` is mounted beside it).
 */

/** Where "Open in KPI Manager" goes. The KPI's own page is member-readable. */
export function kpiManagerHref(kpiId: string): string {
  return `/settings/kpi/${encodeURIComponent(kpiId)}`;
}

const PAGE_SIZE = 20;

/**
 * The tenant's ACTIVE KPI catalogue, searched server-side and paged by cursor.
 *
 * Cursor-paged rather than "fetch everything and filter": the tenant catalogue
 * is unbounded, and a picker must never be the thing that downloads it. Reads
 * start only once an editor calls `activate`, so opening a canvas costs nothing.
 */
function useTenantKpiCatalog(): KpiCatalog {
  const config = useApiClient();
  const cacheScope = useKpiCacheScope();
  // KPI Manager off (ADR-0100): a picker that still opens reads nothing and
  // says why. "Add widget" no longer offers KPI widgets, so this is the rare
  // editor of a tile already on the page.
  const kpiOff = useModuleAvailability().unavailableMessage("kpi");
  const [activated, setActive] = useState(false);
  const active = activated && kpiOff === null;
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<readonly string[]>([]);

  const activate = useCallback((ids: readonly string[]) => {
    setActive(true);
    setSelectedIds((previous) => {
      const next = [...new Set(ids)];
      return previous.length === next.length &&
        previous.every((id, index) => id === next[index])
        ? previous
        : next;
    });
  }, []);

  const page = useInfiniteQuery<KpiCatalogPage>({
    queryKey: ["kpi-catalog", ...cacheScope, "search", searchQuery],
    queryFn: ({ pageParam, signal }) =>
      listKpiCatalogPage(config, {
        ...(searchQuery.trim() ? { q: searchQuery.trim() } : {}),
        ...(typeof pageParam === "string" ? { cursor: pageParam } : {}),
        limit: PAGE_SIZE,
        signal,
      }),
    initialPageParam: undefined as string | undefined,
    // `hasMore` without a cursor is a malformed page; stop rather than loop.
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: active,
    staleTime: 30_000,
    retry: false,
  });

  // A tile may name a KPI the active list no longer contains — archived, or
  // simply past the first page. Resolving the SELECTION by id is what stops the
  // picker showing "nothing chosen" over a tile that plainly has a KPI.
  const selection = useQuery<{
    readonly kpi: KpiDef;
    readonly archived: boolean;
  }>({
    queryKey: ["kpi-catalog", ...cacheScope, "selection", selectedIds[0] ?? ""],
    queryFn: () => getKpiCatalogItem(config, selectedIds[0]!),
    enabled: active && selectedIds.length === 1,
    staleTime: 30_000,
    retry: false,
  });

  const listed = useMemo(
    () => (page.data?.pages ?? []).flatMap((p) => p.items),
    [page.data],
  );
  const chosen = selection.data;

  return useMemo<KpiCatalog>(() => {
    const kpis =
      chosen && !listed.some((kpi) => kpi.id === chosen.kpi.id)
        ? [chosen.kpi, ...listed]
        : listed;
    return {
      kpis,
      archivedKpiIds: chosen?.archived ? [chosen.kpi.id] : [],
      isLoading: page.isPending && page.isFetching,
      isLoadingMore: page.isFetchingNextPage,
      error:
        kpiOff ??
        (page.isError || selection.isError
          ? "KPI choices couldn’t be loaded."
          : null),
      hasMore: page.hasNextPage,
      searchQuery,
      activate,
      onSearchChange: setSearchQuery,
      onLoadMore: () => {
        if (page.hasNextPage && !page.isFetchingNextPage) {
          void page.fetchNextPage();
        }
      },
      onRetry: () => {
        void page.refetch();
        if (selection.isError) void selection.refetch();
      },
    };
  }, [chosen, listed, page, selection, searchQuery, activate, kpiOff]);
}

/** Adapts the shared KPI read to the design system's data-blind seam. */
function useResolvedKpiSource(
  kpiId: string,
  range: KpiTileRange,
): KpiSourceResult {
  const result = useKpiSource(kpiId, range);
  const navigate = useNavigate();
  if (result.status !== "ready" || result.kpi === null) {
    return {
      status: result.status,
      kpi: null,
      message: result.message ?? undefined,
      // No "Try again" when nothing can change by retrying (KPI Manager off).
      ...(result.retryable ? { onRetry: result.refetch } : {}),
    };
  }
  const kpi = result.kpi;
  return {
    status: "ready",
    kpi: {
      kpiId: kpi.kpiId,
      name: kpi.name,
      unit: kpi.unit,
      higherIsBetter: kpi.higherIsBetter,
      targetRange: kpi.targetRange ?? false,
      currentValue: kpi.currentValue,
      currentValueExact: kpi.currentValueExact ?? null,
      observedAt: kpi.observedAt,
      measurementStatus: kpi.measurementStatus,
      periodLabel: result.periodLabel,
      target: kpi.target,
      targetExact: kpi.targetExact ?? null,
      targetDate: kpi.targetDate,
      readings: kpi.readings,
      archived: kpi.archived,
      href: kpiManagerHref(kpi.kpiId),
      onNavigate: (href) => navigate(href),
    },
  };
}

export function KpiWidgetWiring({
  children,
}: {
  readonly children: ReactNode;
}): ReactElement {
  const catalog = useTenantKpiCatalog();
  const resolver = useMemo<KpiSourceResolver>(
    () => ({ useKpiSource: useResolvedKpiSource }),
    [],
  );
  return (
    <KpiCatalogProvider value={catalog}>
      <KpiSourceResolverProvider value={resolver}>
        {children}
      </KpiSourceResolverProvider>
    </KpiCatalogProvider>
  );
}
